import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../../shared/api/client";
import { awaitingAfterDevelopment } from "../../workitems/workItemVocabulary";
import {
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  ErrorState,
  LoadingState,
  StatusBadge,
} from "../../../shared/ui";
import type { MissedDeadlineReport, MissedDeadlineRow } from "../../../shared/types/api";
import { Figure } from "./reportBits";
import { FilterBar, defaultFilters, toQuery, type ReportFilterValues } from "./reportFilters";

/**
 * Who missed a deadline: late completions and still-open work already past its date, worst first.
 *
 * <p>A deadline is the committed date that was agreed, falling back to the original plan. Both a
 * finished-late item and an overdue-open one are shown, because "handed in late" and "not handed
 * in at all" are different problems and rolling them into one number hides which is which.
 */
export function MissedDeadlines() {
  const [filters, setFilters] = useState<ReportFilterValues>(defaultFilters);

  const report = useQuery({
    queryKey: ["report", "missed-deadlines", filters],
    queryFn: () =>
      api.get<MissedDeadlineReport>(`/api/v1/reports/missed-deadlines?${toQuery(filters)}`),
  });

  return (
    <div className="space-y-4">
      <FilterBar values={filters} onChange={setFilters} />

      {report.isPending ? (
        <LoadingState label="Finding what slipped" />
      ) : report.isError ? (
        <ErrorState error={report.error} onRetry={() => void report.refetch()} />
      ) : (
        <Report data={report.data} />
      )}
    </div>
  );
}

function Report({ data }: { data: MissedDeadlineReport }) {
  return (
    <>
      <div className="grid grid-cols-3 gap-3">
        <Figure label="People affected" value={String(data.peopleCount)} note="with a slip in range" />
        <Figure
          label="Finished late"
          value={String(data.lateCompletedTotal)}
          note="past the agreed date"
          tone={data.lateCompletedTotal > 0 ? "amber" : undefined}
        />
        <Figure
          label="Overdue, still open"
          value={String(data.overdueOpenTotal)}
          note="past due, not done"
          tone={data.overdueOpenTotal > 0 ? "red" : undefined}
        />
      </div>

      <Card>
        <CardHeader title="By person" subtitle="Longest single slip first." />
        <CardBody className="p-0">
          {data.people.length === 0 ? (
            <EmptyState
              title="Nobody missed a deadline"
              message="No late or overdue work in this window and filter."
            />
          ) : (
            <ul className="divide-y divide-ink-100">
              {data.people.map((row) => (
                <PersonRow key={row.employeeId} row={row} />
              ))}
            </ul>
          )}
        </CardBody>
      </Card>
    </>
  );
}

function PersonRow({ row }: { row: MissedDeadlineRow }) {
  return (
    <li className="px-4 py-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="text-sm text-ink-900">
          {row.employeeName}
          {row.department && <span className="ml-2 text-xs text-ink-500">{row.department}</span>}
        </span>
        <span className="flex items-center gap-2 text-xs">
          {row.lateCompletedCount > 0 && (
            <StatusBadge tone="amber">{row.lateCompletedCount} late</StatusBadge>
          )}
          {row.overdueOpenCount > 0 && (
            <StatusBadge tone="red">{row.overdueOpenCount} overdue</StatusBadge>
          )}
          <span className="tnum text-ink-500">worst {row.worstDaysLate}d</span>
        </span>
      </div>
      <ul className="mt-2 space-y-1">
        {row.items.map((item) => (
          <li key={item.workItemId} className="flex flex-wrap items-baseline gap-2 text-xs">
            <Link
              to={`/work-items/${item.itemKey}`}
              className="font-mono text-ink-500 hover:text-brand-600"
            >
              {item.itemKey}
            </Link>
            <span className="text-ink-700">{item.title}</span>
            <span className="text-ink-400">due {item.deadline ?? "—"}</span>
            <span className={item.stillOpen ? "text-red-700" : "text-amber-700"}>
              {item.stillOpen ? "still open · " : `development done ${item.completedDate} · `}
              {item.daysLate}d late
            </span>
            {/* The deadline was measured at the end of development, so this item can be late and
                still be moving. Saying what it waits on stops the line reading as a stall. */}
            {awaitingAfterDevelopment(item.status) && (
              <span className="text-ink-500">· {awaitingAfterDevelopment(item.status)}</span>
            )}
          </li>
        ))}
      </ul>
    </li>
  );
}
